// ─────────────────────────────────────────────────────────────────────────────
// components/SolutionHeader.tsx
// Top bar: title, impact badge, edit / save / discard controls, vault button,
// approval chip, and close button.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import { EditType } from '../types';
import { getApprovalStatusConfig } from '../solutionUtils';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';

interface SolutionHeaderProps {
  displayData: any;
  effectiveIsIdeation: boolean;
  isEditable: boolean;
  isProcessDiagramActive: boolean;
  isEditing: boolean;
  approvalStatus: string | null;
  localVaultState: boolean;
  isVaultActionLoading: boolean;
  isProcessAiOpportunities: boolean;
  closeDrawer?: () => void;
  onEdit: (type: EditType) => void;
  onSave: () => void;
  onDiscard: () => void;
  onDelete: () => void;
  onSendForApproval: () => void;
  onVaultClick: () => void;
  onToggleEditing: (value: boolean) => void;
  recommended?: boolean;
  isVersionViewMode?: boolean;
  onRestore?: () => void;
}

const LOCKED_STATUSES = ['solutioning', 'ready_to_build', 'readytobuild', 'build'];

const SolutionHeader: React.FC<SolutionHeaderProps> = ({
  effectiveIsIdeation,
  isEditable,
  isProcessDiagramActive,
  isEditing,
  approvalStatus,
  localVaultState,
  isVaultActionLoading,
  isProcessAiOpportunities,
  closeDrawer,
  onSave,
  onDiscard,
  onDelete,
  onSendForApproval,
  onVaultClick,
  onToggleEditing,
  isVersionViewMode,
  onRestore,
}) => {
  if (isProcessAiOpportunities) return null;

  const isRestoreBlocked = useSelector((state: RootState) => state.newIdeation.is_restore_blocked);

  const restoreTooltipMsg = isRestoreBlocked
    ? 'This version cannot be restored as some opportunities are in Solutioning, Approved, or Ready to Build status.'
    : '';

  const isEditLocked = LOCKED_STATUSES.includes(String(approvalStatus).toLowerCase());
  const statusConfig = getApprovalStatusConfig(approvalStatus);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 },
        p: 2,
        borderBottom: '1px solid #e0e0e0',
        position: { xs: 'sticky', sm: 'relative' },
        top: 0,
        zIndex: 10,
        backgroundColor: '#FFFFFF',
      }}
    >
      <Typography variant="h6">
        {effectiveIsIdeation ? 'AI Ideation Opportunity Details' : 'AI Opportunity Details'}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {isVersionViewMode ? (
          <Tooltip
            title={restoreTooltipMsg}
            arrow
            placement="bottom"
            disableHoverListener={!isRestoreBlocked}
          >
            <span>
              <Button
                variant="contained"
                onClick={isRestoreBlocked ? undefined : onRestore}
                disabled={isRestoreBlocked}
                sx={{
                  textTransform: 'none',
                  bgcolor: isRestoreBlocked ? '#B0BEC5' : '#2054F4',
                  borderRadius: '6px',
                  px: 3,
                  py: 0.8,
                  cursor: isRestoreBlocked ? 'not-allowed' : 'pointer',
                  '&:hover': { bgcolor: isRestoreBlocked ? '#B0BEC5' : '#1A45C6' },
                  '&.Mui-disabled': {
                    bgcolor: '#B0BEC5',
                    color: '#FFFFFF',
                  },
                }}
              >
                Restore
              </Button>
            </span>
          </Tooltip>
        ) : (
          isEditable && isProcessDiagramActive && (
            <>
              {/* Send for approval (only when not editing) */}
              {!isEditing &&
                isProcessDiagramActive &&
                (approvalStatus === 'draft' ||
                  approvalStatus === 'created' ||
                  approvalStatus === 'rejected') && (
                  <Button
                    variant="text"
                    onClick={onSendForApproval}
                    sx={{ textTransform: 'none', color: '#1A55F9', fontWeight: 500, px: 2 }}
                    startIcon={<CheckCircleOutline />}
                  >
                    Send for approval
                  </Button>
                )}

              {/* Approval status chip */}
              {isEditing && effectiveIsIdeation && statusConfig.show && (
                <Chip
                  label={statusConfig.text}
                  sx={{
                    height: '28px',
                    borderRadius: '4px',
                    fontFamily: 'Poppins',
                    fontWeight: 500,
                    fontSize: '12px',
                    color: statusConfig.color,
                    backgroundColor: statusConfig.bgColor,
                    border: 'none',
                    '& .MuiChip-label': { padding: '1px 6px' },
                  }}
                />
              )}

              {/* Edit / Save+Discard toggle */}
              {!isEditing ? (
                <Tooltip
                  title={
                    isEditLocked
                      ? 'This opportunity is locked. It is currently being processed in Solution XPLR.'
                      : ''
                  }
                  placement="bottom"
                  arrow
                >
                  <Box
                    sx={{
                      color: isEditLocked ? '#B9B9B9' : '#1A55F9',
                      cursor: isEditLocked ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      pointerEvents: 'auto',
                    }}
                    onClick={() => {
                      if (!isEditLocked) onToggleEditing(true);
                    }}
                  >
                    <img
                      src="/images/edit-border.png"
                      alt="edit"
                      width={14}
                      style={{ opacity: isEditLocked ? 0.4 : 1 }}
                    />
                    <Typography
                      variant="button"
                      sx={{
                        textTransform: 'capitalize',
                        color: isEditLocked ? '#B9B9B9' : '#1A55F9',
                      }}
                    >
                      Edit
                    </Typography>
                  </Box>
                </Tooltip>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button variant="outlined" size="small" onClick={onDiscard}>
                    Discard
                  </Button>
                  <Button variant="contained" size="small" onClick={onSave}>
                    Save
                  </Button>
                </Box>
              )}

              {/* Delete */}
              {!isEditing &&
                isProcessDiagramActive &&
                (approvalStatus === 'draft' || approvalStatus === 'created') && (
                  <Button
                    variant="text"
                    onClick={onDelete}
                    sx={{ textTransform: 'none', color: '#EF4444', fontWeight: 500, px: 2 }}
                    startIcon={<img src="/images/removeOpportunity.svg" alt="delete" />}
                  >
                    Remove
                  </Button>
                )}
            </>
          )
        )}

        {/* Vault button (shown when process diagram is NOT active) */}
        {!isProcessDiagramActive && (
          <Button
            variant="outlined"
            startIcon={<img src="/images/Vault.png" alt="vault" />}
            onClick={onVaultClick}
            disabled={isVaultActionLoading}
            sx={{ textTransform: 'none', fontSize: '14px', fontWeight: 500 }}
          >
            {`${localVaultState ? 'Remove from' : 'Add to'} my list`}
            {isVaultActionLoading && <span style={{ marginLeft: 8, opacity: 0.7 }}>…</span>}
          </Button>
        )}

        {closeDrawer && <CloseIcon onClick={closeDrawer} sx={{ cursor: 'pointer' }} />}
      </Box>
    </Box>
  );
};

export default SolutionHeader;
